/**
 * VoiceCallSettings — Doctor Dashboard tab block.
 *
 * Lets a doctor:
 *   - Toggle voice calls ON/OFF (Turbo only)
 *   - Choose Auto vs Manual approval mode
 *   - Set availability hours + days
 *   - Delegate ringing to a receptionist (DocSite user lookup by email)
 *   - Choose whether the doctor's own device should ring alongside the delegate
 */
import React, { useEffect, useState } from 'react';
import { Phone, Loader2, Save, X, AlertCircle, CheckCircle2, Clock, UserPlus, Lock } from 'lucide-react';
import api from '@/lib/api';

const DAYS = [
  { id: 'mon', label: 'Mon' }, { id: 'tue', label: 'Tue' }, { id: 'wed', label: 'Wed' },
  { id: 'thu', label: 'Thu' }, { id: 'fri', label: 'Fri' }, { id: 'sat', label: 'Sat' }, { id: 'sun', label: 'Sun' },
];

export default function VoiceCallSettings() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [turboActive, setTurboActive] = useState(false);
  const [enabled, setEnabled] = useState(false);
  const [mode, setMode] = useState('auto');
  const [hoursEnabled, setHoursEnabled] = useState(false);
  const [hoursStart, setHoursStart] = useState('09:00');
  const [hoursEnd, setHoursEnd] = useState('20:00');
  const [days, setDays] = useState(['mon', 'tue', 'wed', 'thu', 'fri', 'sat']);
  const [ringDoctorToo, setRingDoctorToo] = useState(true);
  const [delegate, setDelegate] = useState(null);
  const [delegateEmailInput, setDelegateEmailInput] = useState('');

  const load = async () => {
    setLoading(true); setError('');
    try {
      const r = await api.get('/voice/settings');
      setTurboActive(r.data.turbo_active);
      const s = r.data.settings;
      setEnabled(s.enabled);
      setMode(s.mode || 'auto');
      setHoursEnabled(s.hours?.enabled || false);
      setHoursStart(s.hours?.start || '09:00');
      setHoursEnd(s.hours?.end || '20:00');
      setDays(s.hours?.days || ['mon', 'tue', 'wed', 'thu', 'fri', 'sat']);
      setRingDoctorToo(s.ring_doctor_too !== false);
      setDelegate(s.delegate);
      setDelegateEmailInput(s.delegate?.email || '');
    } catch (e) {
      // 404 = voice routes not deployed yet (production catching up). Silently
      // fall back to defaults so the doctor sees a clean empty state.
      if (e?.response?.status === 404) {
        setTurboActive(false);
      } else {
        setError(e?.response?.data?.detail || 'Could not load settings');
      }
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true); setError(''); setSuccess('');
    try {
      await api.put('/voice/settings', {
        enabled,
        mode,
        hours_enabled: hoursEnabled,
        hours_start: hoursStart,
        hours_end: hoursEnd,
        hours_days: days,
        delegate_email: delegateEmailInput.trim() || null,
        ring_doctor_too: ringDoctorToo,
      });
      setSuccess('Saved');
      await load();
      setTimeout(() => setSuccess(''), 2500);
    } catch (e) {
      if (e?.response?.status === 404) {
        setError('Voice calls module not yet deployed to this environment. Redeploy to enable.');
      } else {
        setError(e?.response?.data?.detail || 'Could not save');
      }
    } finally { setSaving(false); }
  };

  const removeDelegate = () => { setDelegateEmailInput(''); setDelegate(null); };

  const toggleDay = (id) => {
    setDays((d) => d.includes(id) ? d.filter((x) => x !== id) : [...d, id]);
  };

  if (loading) {
    return <div className="p-8 flex items-center gap-2 text-slate-500"><Loader2 size={16} className="animate-spin" />Loading voice settings…</div>;
  }

  return (
    <div className="space-y-6" data-testid="voice-call-settings">
      <div className="flex items-start justify-between">
        <div>
          <h3 className="text-xl font-semibold text-slate-900 flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
            <Phone size={18} className="text-emerald-600" />Voice calls
          </h3>
          <p className="text-sm text-slate-600 mt-1">Patients on your profile can request a 1-tap audio call. Browser-to-browser, your number stays private.</p>
        </div>
        {!turboActive && (
          <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-amber-50 border border-amber-200 text-amber-800 text-[10px] font-bold uppercase tracking-wider">
            <Lock size={10} />Turbo only
          </span>
        )}
      </div>

      {!turboActive && (
        <div className="p-4 bg-amber-50 border border-amber-200 rounded-xl text-sm text-amber-900">
          Voice calls are part of the Turbo plan. <a href="/upgrade" className="font-semibold underline">Upgrade to enable →</a>
        </div>
      )}

      {/* Master toggle */}
      <label className={`flex items-start gap-3 p-4 bg-white border rounded-2xl ${turboActive ? 'border-slate-200 cursor-pointer hover:border-emerald-200' : 'border-slate-100 opacity-60 cursor-not-allowed'}`}>
        <input type="checkbox" checked={enabled} disabled={!turboActive}
          onChange={(e) => setEnabled(e.target.checked)}
          className="mt-1" data-testid="voice-toggle-enabled" />
        <div className="flex-1">
          <p className="font-semibold text-slate-900">Allow voice calls on my profile</p>
          <p className="text-xs text-slate-500 mt-0.5">When OFF, patients see chat & WhatsApp options only.</p>
        </div>
      </label>

      {enabled && turboActive && (
        <>
          {/* Mode */}
          <div className="p-4 bg-white border border-slate-200 rounded-2xl space-y-3">
            <p className="font-semibold text-slate-900 text-sm">How should incoming calls behave?</p>
            <label className="flex items-start gap-3 cursor-pointer">
              <input type="radio" checked={mode === 'auto'} onChange={() => setMode('auto')} className="mt-1" data-testid="voice-mode-auto" />
              <div>
                <p className="text-sm font-medium text-slate-900">Always-open (auto)</p>
                <p className="text-xs text-slate-500">Your phone rings as soon as a patient requests a call. Best for solo practices.</p>
              </div>
            </label>
            <label className="flex items-start gap-3 cursor-pointer">
              <input type="radio" checked={mode === 'manual'} onChange={() => setMode('manual')} className="mt-1" data-testid="voice-mode-manual" />
              <div>
                <p className="text-sm font-medium text-slate-900">Manual approve</p>
                <p className="text-xs text-slate-500">Each request waits in your inbox. You decide when to ring.</p>
              </div>
            </label>
          </div>

          {/* Hours */}
          <div className="p-4 bg-white border border-slate-200 rounded-2xl space-y-3">
            <label className="flex items-center gap-3 cursor-pointer">
              <input type="checkbox" checked={hoursEnabled} onChange={(e) => setHoursEnabled(e.target.checked)} data-testid="voice-hours-enabled" />
              <div>
                <p className="font-semibold text-slate-900 text-sm flex items-center gap-1.5"><Clock size={13} />Restrict to specific hours</p>
                <p className="text-xs text-slate-500">Outside these hours, callers see "Doctor is currently offline".</p>
              </div>
            </label>
            {hoursEnabled && (
              <div className="space-y-3 pl-7">
                <div className="flex items-center gap-3">
                  <input type="time" value={hoursStart} onChange={(e) => setHoursStart(e.target.value)} className="px-3 py-2 rounded-xl border border-slate-200 text-sm" data-testid="voice-hours-start" />
                  <span className="text-slate-400">to</span>
                  <input type="time" value={hoursEnd} onChange={(e) => setHoursEnd(e.target.value)} className="px-3 py-2 rounded-xl border border-slate-200 text-sm" data-testid="voice-hours-end" />
                </div>
                <div className="flex flex-wrap gap-2">
                  {DAYS.map((d) => (
                    <button type="button" key={d.id} onClick={() => toggleDay(d.id)}
                      className={`px-3 py-1.5 rounded-full text-xs font-medium border ${days.includes(d.id) ? 'bg-emerald-600 text-white border-emerald-600' : 'bg-white text-slate-600 border-slate-200 hover:border-slate-400'}`}
                      data-testid={`voice-day-${d.id}`}>
                      {d.label}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Delegate */}
          <div className="p-4 bg-white border border-slate-200 rounded-2xl space-y-3">
            <p className="font-semibold text-slate-900 text-sm flex items-center gap-1.5"><UserPlus size={13} />Forward calls to a receptionist (optional)</p>
            <p className="text-xs text-slate-500">Enter the email of an existing DocSite account. They'll receive the ringing notification on their installed PWA. Make sure they've registered first.</p>
            <div className="flex flex-col sm:flex-row gap-2">
              <input value={delegateEmailInput} onChange={(e) => setDelegateEmailInput(e.target.value)} type="email"
                className="flex-1 px-3 py-2 rounded-xl border border-slate-200 text-sm focus:border-emerald-400 focus:ring-2 focus:ring-emerald-100 outline-none"
                placeholder="receptionist@example.com" data-testid="voice-delegate-email" />
              {delegate && (
                <button type="button" onClick={removeDelegate}
                  className="inline-flex items-center gap-1 px-3 py-2 text-xs text-rose-600 hover:bg-rose-50 rounded-xl"
                  data-testid="voice-delegate-remove">
                  <X size={12} />Clear
                </button>
              )}
            </div>
            {delegate && (
              <div className="text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-xl px-3 py-2 flex items-center gap-1.5" data-testid="voice-delegate-active">
                <CheckCircle2 size={12} />Active delegate: <b>{delegate.name || delegate.email}</b>
              </div>
            )}
            <label className="flex items-center gap-2 cursor-pointer pt-2">
              <input type="checkbox" checked={ringDoctorToo} onChange={(e) => setRingDoctorToo(e.target.checked)} data-testid="voice-ring-doctor-too" />
              <span className="text-xs text-slate-600">Ring my device <b>too</b> alongside the delegate</span>
            </label>
          </div>
        </>
      )}

      {/* Save */}
      <div className="flex items-center gap-3">
        <button onClick={save} disabled={saving}
          className="inline-flex items-center gap-2 px-5 py-2.5 bg-emerald-600 text-white rounded-xl text-sm font-semibold hover:bg-emerald-700 disabled:opacity-60"
          data-testid="voice-settings-save">
          {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}Save settings
        </button>
        {success && <span className="text-xs text-emerald-700 font-medium flex items-center gap-1"><CheckCircle2 size={12} />{success}</span>}
        {error && <span className="text-xs text-rose-600 font-medium flex items-center gap-1"><AlertCircle size={12} />{error}</span>}
      </div>
    </div>
  );
}
